import React, { useState, useEffect, useCallback } from 'react';
import { getPlanningList, getWeeks, BUSINESS_UNITS } from '../../services/api';
import NewPlanningModal from './NewPlanningModal';
import PlanningEditor from './PlanningEditor';
import PlanningDetail from './PlanningDetail';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

const COLUMNS = [
  { key: 'lot_name', label: 'Nombre de Lote', sortable: true },
  { key: 'week_name', label: 'Semana', sortable: true },
  { key: 'crop_name', label: 'Cultivo', sortable: true },
  { key: 'area', label: 'Área', sortable: false },
  { key: 'total_activities', label: 'Actividades', sortable: true },
  { key: 'people_in_planning', label: 'Colaboradores', sortable: true },
  { key: 'completed_percent', label: 'Avance', sortable: true },
];

export default function PlanningList() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [businessUnitId, setBusinessUnitId] = useState(1);
  const [selectedWeekId, setSelectedWeekId] = useState('');
  const [weeks, setWeeks] = useState([]);
  const [loadingWeeks, setLoadingWeeks] = useState(false);

  // Table state
  const [sortKey, setSortKey] = useState('lot_name');
  const [sortDir, setSortDir] = useState('asc');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const LIMIT = 25;

  // Modals
  const [showNewModal, setShowNewModal] = useState(false);
  const [editorConfig, setEditorConfig] = useState(null);
  const [selectedPlanning, setSelectedPlanning] = useState(null);

  // Load weeks when BU changes
  useEffect(() => {
    setLoadingWeeks(true);
    setSelectedWeekId('');
    setPage(0);  // resetear paginador al cambiar unidad de negocio
    getWeeks(businessUnitId)
      .then(res => {
        // getWeeks ya devuelve la lista filtrada (1 atrás + 5 adelante)
        const list = Array.isArray(res) ? res : res.data || [];
        setWeeks(list);
      })
      .catch(() => setWeeks([]))
      .finally(() => setLoadingWeeks(false));
  }, [businessUnitId]);

  // Load planning data — depende de TODOS los filtros activos
  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        business_unit_id: businessUnitId,
        order_by: `${sortKey}.${sortDir}`,
        column: sortKey,
        direction: sortDir,
        limit: LIMIT,
        offset: page * LIMIT,
        embed: 'crops,crops.crop,products,products.product',
        __logic: 'AND',
      };
      if (selectedWeekId) params.week_id = selectedWeekId;
      const res = await getPlanningList(params);
      const payload = Array.isArray(res) ? res[0] : res;
      setData(payload?.data || []);
      setTotal(payload?.total_count || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [businessUnitId, sortKey, sortDir, page, selectedWeekId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSort = (key) => {
    if (!COLUMNS.find(c => c.key === key)?.sortable) return;
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
    setPage(0);
  };

  const filtered = data.filter(row =>
    !search || [row.lot_name, row.week_name, row.crop_name]
      .some(v => v?.toLowerCase().includes(search.toLowerCase()))
  );

  // Si hay planeación seleccionada, mostrar detalle
  if (selectedPlanning) {
    return <PlanningDetail planning={selectedPlanning} onBack={() => setSelectedPlanning(null)} />;
  }

  // If editor is active, render it
  if (editorConfig) {
    return <PlanningEditor config={editorConfig} onBack={() => { setEditorConfig(null); loadData(); }} />;
  }

  return (
    <div style={styles.page}>
      {/* Page header */}
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.title}>Planeación Diaria</h1>
          <p style={styles.subtitle}>Gestión de semanas de planeación por cultivo</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNewModal(true)}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          + Nueva planeación
        </button>
      </div>

      {/* Filters */}
      <div style={styles.filters}>
        {/* Search */}
        <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: 300 }}>
          <svg
            width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}
          >
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            className="input-field"
            style={{ paddingLeft: 34 }}
            placeholder="Buscar lote, cultivo, semana..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* Business unit */}
        <select
          className="select-field"
          style={{ flex: '0 0 160px' }}
          value={businessUnitId}
          onChange={e => { setBusinessUnitId(Number(e.target.value)); setPage(0); }}
        >
          {BUSINESS_UNITS.map(b => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>

        {/* Week filter */}
        <select
          className="select-field"
          style={{ flex: '0 0 200px' }}
          value={selectedWeekId}
          onChange={e => { setSelectedWeekId(e.target.value); setPage(0); }}
          disabled={loadingWeeks}
        >
          <option value="">Todas las semanas</option>
          {weeks.map(w => (
            <option key={w.id} value={w.id}>
              {w.name} ({w.from_date?.slice(5)} – {w.to_date?.slice(5)})
            </option>
          ))}
        </select>
      </div>

      {/* Error */}
      {error && <div className="alert alert-error" style={{ margin: '0 32px' }}>{error}</div>}

      {/* Table */}
      <div style={{ margin: '0 32px' }}>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                {COLUMNS.map(col => (
                  <th
                    key={col.key}
                    className={col.sortable ? 'sortable' : ''}
                    onClick={() => col.sortable && handleSort(col.key)}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {col.label}
                      {col.sortable && sortKey === col.key && (
                        <span style={{ fontSize: 10, color: 'var(--accent)' }}>
                          {sortDir === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    {COLUMNS.map((_, j) => (
                      <td key={j}>
                        <div className="skeleton" style={{ height: 14, width: '80%', borderRadius: 4 }} />
                      </td>
                    ))}
                  </tr>
                ))
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>
                    No se encontraron planeaciones
                  </td>
                </tr>
              )}
              {!loading && filtered.map(row => (
                <tr
                  key={row.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedPlanning(row)}
                  onMouseEnter={e => e.currentTarget.style.outline = '1px solid rgba(245,197,24,0.2)'}
                  onMouseLeave={e => e.currentTarget.style.outline = 'none'}
                >
                  <td style={{ fontWeight: 500 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {row.lot_name || '—'}
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--text-muted)', flexShrink: 0 }}>
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </span>
                  </td>
                  <td>
                    <span className="badge badge-blue">{row.week_name || '—'}</span>
                  </td>
                  <td>
                    <span className="badge badge-green">{row.crop_name || '—'}</span>
                  </td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                    {row.business_unit_name || '—'}
                  </td>
                  <td style={{ textAlign: 'center', fontWeight: 600 }}>
                    {row.total_activities ?? '—'}
                  </td>
                  <td style={{ textAlign: 'center', fontWeight: 600 }}>
                    {row.people_in_planning != null ? Math.round(row.people_in_planning) : '—'}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={styles.progressBar}>
                        <div style={{
                          ...styles.progressFill,
                          width: `${row.completed_percent || 0}%`,
                          background: row.completed_percent > 80 ? 'var(--accent-green)' : 'var(--accent)',
                        }} />
                      </div>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 30 }}>
                        {row.completed_percent || 0}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination — siempre visible si hay datos */}
        {!loading && total > 0 && (
          <div style={styles.pagination}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Mostrando {page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, total)} de <strong style={{ color: 'var(--text-primary)' }}>{total}</strong> planeaciones
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                className="btn btn-ghost btn-sm"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
              >
                ← Anterior
              </button>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '0 4px' }}>
                Pág. {page + 1} / {Math.ceil(total / LIMIT)}
              </span>
              <button
                className="btn btn-ghost btn-sm"
                disabled={(page + 1) * LIMIT >= total}
                onClick={() => setPage(p => p + 1)}
              >
                Siguiente →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* New planning modal */}
      {showNewModal && (
        <NewPlanningModal
          onClose={() => setShowNewModal(false)}
          onCreated={(cfg) => {
            setShowNewModal(false);
            setEditorConfig(cfg);
          }}
        />
      )}
    </div>
  );
}

const styles = {
  page: {
    padding: '28px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 32px',
    flexWrap: 'wrap',
    gap: 12,
  },
  title: { fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' },
  subtitle: { fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 },
  filters: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '0 32px',
    flexWrap: 'wrap',
  },
  progressBar: {
    flex: 1,
    height: 5,
    background: 'var(--border-light)',
    borderRadius: 3,
    overflow: 'hidden',
    minWidth: 60,
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
    transition: 'width 0.4s ease',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderTop: '1px solid var(--border)',
    marginTop: -1,
    background: 'var(--bg-content, #fff)',
    borderRadius: '0 0 var(--radius-lg) var(--radius-lg)',
  },
};
